const STORAGE_WIDTH = 'pikly-chat-width';
const STORAGE_COLLAPSED = 'pikly-chat-collapsed';
const STORAGE_CONVERSATION = 'pikly-chat-conversation';

export function getStoredChatWidth(defaultWidth = 380) {
  const raw = localStorage.getItem(STORAGE_WIDTH);
  const value = Number(raw);
  return Number.isFinite(value) && value >= 280 ? value : defaultWidth;
}

export function setStoredChatWidth(width) {
  localStorage.setItem(STORAGE_WIDTH, String(Math.round(width)));
}

export function getStoredChatCollapsed() {
  return localStorage.getItem(STORAGE_COLLAPSED) === '1';
}

export function setStoredChatCollapsed(collapsed) {
  localStorage.setItem(STORAGE_COLLAPSED, collapsed ? '1' : '0');
}

export function getStoredConversationId() {
  const raw = localStorage.getItem(STORAGE_CONVERSATION);
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
}

export function setStoredConversationId(id) {
  if (id) {
    localStorage.setItem(STORAGE_CONVERSATION, String(id));
  } else {
    localStorage.removeItem(STORAGE_CONVERSATION);
  }
}

const STORAGE_DRAFTS = 'pikly-chat-drafts';

function draftKey(conversationId) {
  return conversationId ? String(conversationId) : 'new';
}

function readDrafts() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_DRAFTS) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function getStoredDraft(conversationId) {
  const text = readDrafts()[draftKey(conversationId)];
  return typeof text === 'string' ? text : '';
}

export function setStoredDraft(conversationId, text) {
  const drafts = readDrafts();
  const key = draftKey(conversationId);
  const value = (text || '').slice(0, 8000);
  if (!value.trim()) {
    delete drafts[key];
  } else {
    drafts[key] = value;
  }
  localStorage.setItem(STORAGE_DRAFTS, JSON.stringify(drafts));
}

export function removeStoredDraft(conversationId) {
  const drafts = readDrafts();
  delete drafts[draftKey(conversationId)];
  localStorage.setItem(STORAGE_DRAFTS, JSON.stringify(drafts));
}

export async function copyText(text) {
  const value = text || '';
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    if (!ok) {
      throw new Error('copy failed');
    }
    return true;
  }
}

function parseApiError(payload, fallback) {
  if (!payload) return fallback;
  const detail = payload.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  if (payload.message) return payload.message;
  return fallback;
}

export async function fetchChatSettings() {
  const response = await fetch('/api/chat/settings', { credentials: 'include' });
  if (response.status === 403) {
    return { enabled: false, model: '', models: [], show_model_select: false, usage: null };
  }
  if (!response.ok) {
    throw new Error('Не удалось загрузить настройки чата');
  }
  return response.json();
}

export async function updateChatSettings(model) {
  const response = await fetch('/api/chat/settings', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model }),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(parseApiError(payload, 'Не удалось сохранить модель'));
  }
  return response.json();
}

export async function fetchConversations() {
  const response = await fetch('/api/chat/conversations', { credentials: 'include' });
  if (!response.ok) {
    throw new Error('Не удалось загрузить историю чатов');
  }
  return response.json();
}

export async function fetchConversation(id) {
  const response = await fetch(`/api/chat/conversations/${id}`, { credentials: 'include' });
  if (!response.ok) {
    throw new Error('Диалог не найден');
  }
  return response.json();
}

export async function cancelChatStream(streamId) {
  const query = streamId ? `?stream_id=${encodeURIComponent(streamId)}` : '';
  const response = await fetch(`/api/chat/cancel${query}`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Не удалось остановить ответ');
  }
  return response.json();
}

export async function deleteConversation(id) {
  const response = await fetch(`/api/chat/conversations/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Не удалось удалить диалог');
  }
  return response.json();
}

export async function streamChatMessage({
  conversationId,
  content,
  image,
  model,
  signal,
  onMeta,
  onDelta,
  onDone,
  onBalance,
  onStatus,
  onError,
}) {
  const response = await fetch('/api/chat/stream', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({
      content,
      conversation_id: conversationId || null,
      model: model || undefined,
      image: image || undefined,
    }),
    signal,
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(parseApiError(payload, 'Не удалось отправить сообщение'));
  }

  if (!response.body) {
    throw new Error('Поток ответа недоступен');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const handleEvent = (raw) => {
    const dataLine = raw
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line.startsWith('data:'));
    if (!dataLine) return;
    const jsonStr = dataLine.slice(5).trim();
    if (!jsonStr) return;
    let event;
    try {
      event = JSON.parse(jsonStr);
    } catch {
      return;
    }
    if (event.type === 'meta' && onMeta) onMeta(event);
    else if (event.type === 'delta' && onDelta) onDelta(event.content || '');
    else if (event.type === 'status' && onStatus) onStatus(event.message || '');
    else if (event.type === 'balance' && onBalance) onBalance(event.balance);
    else if (event.type === 'done' && onDone) {
      onDone(event.message, event.usage, event.balance, !!event.cancelled);
    }
    else if (event.type === 'error' && onError) onError(event.message || 'Ошибка ответа', event.balance);
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    parts.forEach(handleEvent);
  }

  if (buffer.trim()) {
    handleEvent(buffer);
  }
}
